import { UserModalContext } from './Context'

import { useState, useContext, useEffect } from 'react'
import { useUserStore, INITIAL_STATE } from 'Store/User'
import { useCallbackPrompt } from 'Hooks/useCallbackPrompt'
import { shallow } from 'zustand/shallow'
import { toast } from 'react-toastify'
import { generateImageUrl } from 'Utils/generateImageUrl'
import * as Yup from 'yup'

const INITIAL_PASSWORD_STATE = {
	oldPassword: '',
	newPassword: '',
}

const ViewModel = () => {
	const validationSchema = Yup.object().shape({
		email: Yup.string()
			.email('กรุณากรอกอีเมลให้ถูกต้อง')
			.typeError('กรุณากรอกอีเมล')
			.required('กรุณากรอกอีเมล'),
		fullnameTh: Yup.string().required('กรุณากรอกชื่อ-นามสกุล'),
		fullnameEn: Yup.string().required('กรุณากรอกชื่อ-นามสกุล (ภาษาอังกฤษ)'),
		roleTitle: Yup.string().required('กรุณากรอกชื่อตำแหน่ง'),
	})

	const { open, formType, setOpen, setFormType } = useContext(UserModalContext)
	const getAllUsers = useUserStore(state => state.getAll, shallow)
	const createUser = useUserStore(state => state.create, shallow)
	const { users, selected, selectedId, setSelectedId, setSelected, update, changePassword } =
		useUserStore(
			state => ({
				users: state.data,
				selectedId: state.selectedId,
				selected: state.selected,
				setSelectedId: state.setSelectedId,
				setSelected: state.setSelected,
				update: state.edit,
				changePassword: state.changePassword,
			}),
			shallow
		)

	const [formState, setFormState] = useState(INITIAL_STATE)
	const [passwordState, setPasswordState] = useState(INITIAL_PASSWORD_STATE)
	const [hasChanged, setHasChanged] = useState(false)
	const [error, setError] = useState<any>({})
	const [isSubmitting, setIsSubmitting] = useState(false)

	useCallbackPrompt(hasChanged)

	const fetchData = async () => {
		if (selectedId && formType === 'edit') {
			const finded = users.find(user => user.id === selectedId)
			if (!finded) {
				toast.error('ไม่พบข้อมูลผู้ใช้งาน')
				return
			} else {
				setFormState({
					...finded,
					pictureProfilePath: finded.pictureProfilePath
						? generateImageUrl(finded.pictureProfilePath)
						: null,
				})
				setSelected({
					...finded,
					pictureProfilePath: finded.pictureProfilePath
						? generateImageUrl(finded.pictureProfilePath)
						: null,
				})
			}
		}
	}

	const handleClose = () => {
		setSelectedId('')
		setSelected(INITIAL_STATE)
		setFormState(INITIAL_STATE)
		setPasswordState(INITIAL_PASSWORD_STATE)
		setError({})
		setFormType('add')
		setOpen(false)
	}

	const onChange = (name: string, value: any) => {
		if (name.toLowerCase().includes('password')) {
			setPasswordState({
				...passwordState,
				[name]: value,
			})
		} else {
			setFormState({
				...formState,
				[name]: value,
			})

			validationSchema
				.validate(
					{
						...formState,
						[name]: value,
					},
					{ abortEarly: false }
				)
				.then(() => {
					setError({})
				})
				.catch(err => {
					setError(
						err.inner.reduce((all: any[], e: any) => {
							all[e.path] = e.message

							return all
						}, {})
					)
				})
		}

		setHasChanged(true)
	}

	const handleSubmit = async () => {
		if (formType === 'add') {
			setIsSubmitting(true)
			const requiredFields = ['email', 'fullnameTh', 'fullnameEn']
			const requiredPasswordFields = ['newPassword']
			if (
				requiredFields.some(
					// @ts-ignore
					field => !formState[field] || formState[field] === '' || formState[field] === null
				) ||
				// @ts-ignore
				requiredPasswordFields.some(field => !passwordState[field] || passwordState[field] === '')
			) {
				toast.error('Please fill in all required fields')
				setIsSubmitting(false)
				return
			} else {
				const body: { [key: string]: any } = {}
				Object.keys(formState).forEach(key => {
					if (key === 'id') {
						return
					} else {
						// @ts-ignore
						if (formState[key])
							// @ts-ignore
							body[key] = formState[key]
					}
				})
				body['password'] = passwordState.newPassword

				const { success, data } = await createUser(body)
				if (!success) {
					toast.error(data)
					setIsSubmitting(false)
					return
				} else {
					toast.success('Create user successfully')
					setHasChanged(false)
					await getAllUsers()
					setPasswordState(INITIAL_PASSWORD_STATE)
					setFormState(INITIAL_STATE)

					setTimeout(() => {
						handleClose()
						setIsSubmitting(false)
					}, 500)
				}
			}
		} else if (formType === 'edit') {
			setIsSubmitting(false)
			const body: { [key: string]: any } = {}
			Object.keys(selected).forEach(key => {
				// @ts-ignore

				if (key === 'id') {
					return
				} else if (key === 'profilePicturePath') {
					// Logo is exist
					if (selected.pictureProfilePath) {
						// Logo is a file
						if (formState.pictureProfilePath && typeof formState.pictureProfilePath !== 'string') {
							body[key] = formState.pictureProfilePath
						}
						// Logo is deleted
						else if (!formState.pictureProfilePath) {
							body[key] = null
						}
					}
					// Logo is empty
					else if (!selected.pictureProfilePath) {
						if (formState.pictureProfilePath) {
							body[key] = formState.pictureProfilePath
						}
					}
				} // @ts-ignore
				else if (formState[key] !== selected[key]) {
					// @ts-ignore
					body[key] = formState[key]
				}
			})

			if (passwordState.newPassword.length !== 0 || passwordState.oldPassword.length !== 0) {
				if (passwordState.oldPassword.length === 0 || passwordState.newPassword.length === 0) {
					toast.error('Please fill all required fields')
					return
				} else if (
					Object.keys(passwordState).some(key => {
						if (key === 'oldPassword') return false

						// @ts-ignore
						return passwordState[key].length === 0 || passwordState[key].length < 6
					})
				) {
					toast.error('Password must be at least 6 characters')
					return
				} else {
					const { success: passwordSuccess, data: passwordData } = await changePassword(
						parseInt(selectedId),
						{
							oldPassword: passwordState.oldPassword,
							password: passwordState.newPassword,
						}
					)
					if (!passwordSuccess) {
						toast.error(passwordData)
						return
					} else {
						setPasswordState(INITIAL_PASSWORD_STATE)
						toast.success('Change password successfully')
					}
				}
			}

			if (Object.keys(body).length === 0) {
				setIsSubmitting(false)
				toast.error('No data has been changed')
				return
			} else {
				const { success, data } = await update(parseInt(selectedId), body)
				if (!success) {
					toast.error(data)
					setIsSubmitting(false)
					return
				} else {
					toast.success('Update user successfully')
					setHasChanged(false)
					await getAllUsers()

					setTimeout(() => {
						handleClose()
						setIsSubmitting(false)
					}, 500)
				}
			}
		}
	}

	useEffect(() => {
		if (selectedId) {
			fetchData()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedId])

	return {
		open,
		formType,
		formState,
		passwordState,
		onChange,
		handleClose,
		handleSubmit,
		error,
		isSubmitting,
	}
}

export default ViewModel
